import { useMemo, useState } from 'react'
import {
  Container,
  Paper,
  Typography,
  Box,
  Chip,
  Alert,
  CircularProgress,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
} from '@mui/material'
import { EmojiEvents, Download, Verified, EmojiEventsOutlined } from '@mui/icons-material'
import {
  useGetMyAchievementsQuery,
  useLazyDownloadCertificateQuery,
  type AchievementResult,
} from '../store/api/achievementsApi'
import { useGetTeamsQuery } from '../store/api/teamsApi'
import { useGetCasesQuery } from '../store/api/casesApi'
import { useGetRoundsQuery, RoundType } from '../store/api/roundsApi'
import { useGetPublishedRoundsQuery } from '../store/api/reportsApi'
import { useGetLatestBracketQuery } from '../store/api/bracketsApi'
import { PageHeader } from '../components/PageHeader'
import { getContentPaperSx, getTablePaperSx } from '../theme/sectionSurfaces'
import { useAppSelector } from '../hooks/redux'
import { hasPermission } from '../utils/permissions'

type RoundStage = 'preliminary' | 'semifinal' | 'final'

/** Derive trạng thái team ở mỗi round từ competition_status + final_rank.
 *
 *  Bug fix 2026-05-25: trước đây hàm này derive từ field DB
 *  (`competition_status`, `final_rank`) mà KHÔNG kiểm tra round có thực sự
 *  được công bố qua `publish_round_results` chưa → bảng thành tích hiển thị
 *  "Qua vòng" / "Vô địch" cho dữ liệu seed/test → trông như mock. Giờ thêm
 *  `stagePublished` bắt buộc — nếu vòng tương ứng chưa publish, FE hiển thị
 *  "Chưa công bố" thay cho status derived.
 *
 *  - "advanced" = đã qua vòng đó | "eliminated" = bị loại ở vòng đó
 *  - "champion"/"runner_up"/"third"/"fourth" = vị trí cuối ở chung kết
 *  - "pending" = chưa rõ (chưa publish). */
function deriveStageStatus(
  stage: RoundStage,
  compStatus: string | null | undefined,
  finalRank: number | null | undefined,
  stagePublished: boolean,
): { label: string; color: 'success' | 'error' | 'warning' | 'default' | 'info'; icon?: boolean } {
  if (!stagePublished) {
    return { label: 'Chưa công bố', color: 'default' }
  }
  const cs = compStatus || null
  if (stage === 'preliminary') {
    if (cs === 'advanced_to_semifinal' || cs === 'advanced_to_final') return { label: 'Qua vòng', color: 'success' }
    if (cs === 'eliminated' && !finalRank) return { label: 'Loại', color: 'error' }
    // Nếu có final_rank ≥ 5 nghĩa là đã thi xong sơ loại nhưng không vào top 4
    if (finalRank && finalRank > 4) return { label: 'Loại', color: 'error' }
    if (finalRank && finalRank <= 4) return { label: 'Qua vòng', color: 'success' }
    return { label: 'Chưa công bố', color: 'default' }
  }
  if (stage === 'semifinal') {
    if (cs === 'advanced_to_final') return { label: 'Qua vòng', color: 'success' }
    if (cs === 'advanced_to_semifinal') return { label: 'Đang thi', color: 'info' }
    if (finalRank === 3 || finalRank === 4) return { label: 'Loại (BK)', color: 'error' }
    if (finalRank === 1 || finalRank === 2) return { label: 'Qua vòng', color: 'success' }
    return { label: '—', color: 'default' }
  }
  // final
  if (finalRank === 1) return { label: 'Vô địch', color: 'warning', icon: true }
  if (finalRank === 2) return { label: 'Á quân', color: 'warning' }
  if (cs === 'advanced_to_final' && !finalRank) return { label: 'Đang thi', color: 'info' }
  if ((cs === 'eliminated' && (finalRank === 3 || finalRank === 4)) || finalRank === 3 || finalRank === 4) {
    return { label: 'Không vào CK', color: 'default' }
  }
  return { label: '—', color: 'default' }
}

/** UAT-TC Chung kết #20: Đội thắng xem thành tích & tải chứng chỉ điện tử. */
export default function AchievementsPage() {
  const { data, isLoading, error } = useGetMyAchievementsQuery()
  // YC7 (audit 2026-05-23): sinh viên CHỈ xem hạng, KHÔNG xem điểm cụ thể.
  // Ẩn 2 cột "Điểm TB" + "Tổng điểm" với role student.
  const { user: currentUser } = useAppSelector((state) => state.auth)
  const hideScoreColumns = currentUser?.role === 'student'
  const canViewAllProgress = !!(currentUser && hasPermission(currentUser.role, 'ASSIGN_CASES'))
  const [downloadCert] = useLazyDownloadCertificateQuery()
  // Tiến trình toàn giải (chỉ admin/judge): chọn case → matrix Team × Round.
  // 2026-05-26: poll 15s teams + publishedRounds + bracket để bảng tự cập nhật
  // sau khi BTC publish match (BK/CK) — trước đây phải reload page.
  const { data: cases = [] } = useGetCasesQuery(undefined, { skip: !canViewAllProgress })
  const { data: allTeams = [] } = useGetTeamsQuery(undefined, {
    skip: !canViewAllProgress,
    pollingInterval: 15000,
  })
  const { data: allRounds = [] } = useGetRoundsQuery(undefined, { skip: !canViewAllProgress })
  const [progressCaseId, setProgressCaseId] = useState<string>('')

  const progressTeams = useMemo(() => {
    if (!progressCaseId) return []
    return allTeams.filter((t) => (t.assigned_cases || []).includes(progressCaseId))
  }, [allTeams, progressCaseId])

  // Bug fix 2026-05-25: lấy danh sách round đã publish của case để hiển thị
  // "Chưa công bố" thay cho status derived từ team.competition_status (mock-like
  // khi seed data set sẵn).
  const { data: publishedRoundsData } = useGetPublishedRoundsQuery(progressCaseId, {
    skip: !progressCaseId,
    pollingInterval: 15000,
  })
  const publishedRoundIds = useMemo(
    () => new Set(publishedRoundsData?.published_round_ids || []),
    [publishedRoundsData],
  )
  // 2026-05-26: BK/CK publish PER-MATCH (publish_match_results) — KHÔNG ghi
  // `published_round_ids` (chỉ publish-round full mới push). Vì vậy phải đọc
  // trực tiếp bracket.matches để biết per-team status. Fetch bracket cho
  // SEMIFINAL + FINAL round.
  const semiRoundId = useMemo(() => {
    const r = allRounds.find((rr) => rr.case_id === progressCaseId && rr.round_type === RoundType.SEMIFINAL)
    return r?.id || ''
  }, [allRounds, progressCaseId])
  const finalRoundId = useMemo(() => {
    const r = allRounds.find((rr) => rr.case_id === progressCaseId && rr.round_type === RoundType.FINAL)
    return r?.id || ''
  }, [allRounds, progressCaseId])
  const { data: semiBracket } = useGetLatestBracketQuery(
    { case_id: progressCaseId, round_id: semiRoundId },
    { skip: !progressCaseId || !semiRoundId, pollingInterval: 15000 },
  )
  const { data: finalBracket } = useGetLatestBracketQuery(
    { case_id: progressCaseId, round_id: finalRoundId },
    { skip: !progressCaseId || !finalRoundId, pollingInterval: 15000 },
  )

  // Map team_id → {semi: 'won'|'lost'|'pending', final: ...} từ bracket
  // matches. 'won' = winner_team_id===team_id, 'lost' = team thuộc match đã
  // published nhưng KHÔNG phải winner, 'pending' = team chưa thuộc match
  // hoặc match chưa publish.
  type StageOutcome = 'won' | 'lost' | 'pending'
  const teamStageOutcome = useMemo(() => {
    const m = new Map<string, { semi: StageOutcome; final: StageOutcome }>()
    const apply = (bracket: any, stage: 'semi' | 'final') => {
      if (!bracket?.matches) return
      for (const match of bracket.matches as any[]) {
        const published = match.status === 'published' || !!match.winner_team_id
        const teams = [match.team_plaintiff_id, match.team_defendant_id].filter(Boolean)
        for (const tid of teams) {
          const cur = m.get(tid) || { semi: 'pending' as StageOutcome, final: 'pending' as StageOutcome }
          if (published) {
            cur[stage] = match.winner_team_id === tid ? 'won' : 'lost'
          }
          m.set(tid, cur)
        }
      }
    }
    apply(semiBracket, 'semi')
    apply(finalBracket, 'final')
    return m
  }, [semiBracket, finalBracket])

  // Map stage → round_id thuộc case → check publish status (chỉ dùng cho
  // preliminary). Semifinal/Final dùng teamStageOutcome.
  const stagePublishMap = useMemo(() => {
    const rounds = allRounds.filter((r) => r.case_id === progressCaseId)
    const memorial = rounds.find((r) => r.round_type === RoundType.MEMORIAL)
    return {
      preliminary: !!memorial && publishedRoundIds.has(memorial.id),
    }
  }, [allRounds, progressCaseId, publishedRoundIds])
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error' | 'warning' | 'info'
  }>({ open: false, message: '', severity: 'info' })

  const handleDownload = async (caseId: string, caseTitle: string) => {
    try {
      const blob = await downloadCert(caseId).unwrap()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `Certificate_${caseTitle.replace(/\s+/g, '_')}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      setSnackbar({
        open: true,
        message: 'Đã tải chứng chỉ thành công.',
        severity: 'success',
      })
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: `Tải chứng chỉ thất bại: ${err?.data?.detail || err?.message || ''}`,
        severity: 'error',
      })
    }
  }

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 3, display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Container>
    )
  }

  // Admin/judge có thể KHÔNG thuộc team → API trả 404/empty. Cho phép họ vẫn
  // vào trang để xem "Tiến trình toàn giải" bên dưới.
  if ((error || !data) && !canViewAllProgress) {
    return (
      <Container maxWidth="lg" sx={{ mt: 3 }}>
        <PageHeader title="Thành tích" icon={<EmojiEvents />} />
        <Alert severity="info">
          Bạn chưa thuộc đội thi nào hoặc chưa có kết quả công bố.
        </Alert>
      </Container>
    )
  }

  const summary = data?.summary || []
  const bestRank = summary.reduce((min, r) => (r.rank && r.rank < min ? r.rank : min), 999)

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 } }}>
      <PageHeader
        title="Thành tích"
        subtitle={data ? `${data.team_name} — ${data.university}` : undefined}
        icon={<EmojiEvents />}
      />

      {/* Hero card: hạng cao nhất */}
      {bestRank < 999 && (
        <Paper
          elevation={0}
          sx={(t) => ({
            ...getContentPaperSx(t),
            mb: 3,
            background: 'linear-gradient(135deg, #FFC107 0%, #FF6F00 100%)',
            color: 'white',
          })}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Verified sx={{ fontSize: 56 }} />
            <Box>
              <Typography variant="overline" sx={{ opacity: 0.9 }}>
                Thành tích cao nhất
              </Typography>
              <Typography variant="h3" fontWeight={700}>
                Hạng {bestRank}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Đã tham gia {summary.length} cuộc thi
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Bảng thành tích từng cuộc thi — ẩn với admin/judge nếu họ không có data cá nhân */}
      {data && (
      <Paper elevation={0} sx={(t) => getContentPaperSx(t)}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Lịch sử thi đấu
        </Typography>
        {summary.length === 0 ? (
          <Alert severity="info">Chưa có kết quả công bố nào.</Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={(t) => getTablePaperSx(t)}>
            <Table size="small" sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 240 }}><strong>Cuộc thi</strong></TableCell>
                  <TableCell sx={{ minWidth: 80 }}><strong>Hạng</strong></TableCell>
                  {!hideScoreColumns && (
                    <TableCell sx={{ minWidth: 110 }}><strong>Điểm TB</strong></TableCell>
                  )}
                  {!hideScoreColumns && (
                    <TableCell sx={{ minWidth: 110 }}><strong>Tổng điểm</strong></TableCell>
                  )}
                  <TableCell sx={{ minWidth: 130 }}><strong>Ngày công bố</strong></TableCell>
                  <TableCell align="right" sx={{ minWidth: 160 }}><strong>Chứng chỉ</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summary.map((r: AchievementResult) => (
                  <TableRow key={r.case_id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {r.case_title}
                        {r.advanced && (
                          <Chip label="Vào vòng tiếp" size="small" color="success" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`#${r.rank}`}
                        size="small"
                        color={r.rank === 1 ? 'warning' : r.rank <= 3 ? 'primary' : 'default'}
                      />
                    </TableCell>
                    {!hideScoreColumns && (
                      <TableCell>{r.average_score?.toFixed(1) || '—'}</TableCell>
                    )}
                    {!hideScoreColumns && (
                      <TableCell>{r.total_score?.toFixed(1) || '—'}</TableCell>
                    )}
                    <TableCell>
                      {r.published_at ? new Date(r.published_at).toLocaleDateString('vi-VN') : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Download />}
                        onClick={() => handleDownload(r.case_id, r.case_title)}
                        sx={{ textTransform: 'none' }}
                      >
                        Tải PDF
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      )}

      {canViewAllProgress && (
        <Paper elevation={0} sx={(t) => ({ ...getContentPaperSx(t), mt: 3 })}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
            <EmojiEventsOutlined color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Tiến trình toàn giải
            </Typography>
            <Typography variant="body2" color="text.secondary">
              — trạng thái mỗi đội qua từng vòng (Sơ loại → Bán kết → Chung kết)
            </Typography>
          </Box>
          <FormControl size="small" sx={{ minWidth: 280, mb: 2 }}>
            <InputLabel id="progress-case-label">Chọn cuộc thi</InputLabel>
            <Select
              labelId="progress-case-label"
              label="Chọn cuộc thi"
              value={progressCaseId}
              onChange={(e) => setProgressCaseId(e.target.value)}
            >
              <MenuItem value=""><em>— Chọn cuộc thi —</em></MenuItem>
              {cases.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.title}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {progressCaseId && progressTeams.length === 0 && (
            <Alert severity="info">Cuộc thi này chưa có đội đăng ký.</Alert>
          )}
          {progressCaseId && progressTeams.length > 0 && (
            <TableContainer component={Paper} variant="outlined" sx={(t) => getTablePaperSx(t)}>
              <Table size="small" sx={{ minWidth: 760 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 220 }}><strong>Đội</strong></TableCell>
                    <TableCell sx={{ minWidth: 160 }}><strong>Trường</strong></TableCell>
                    <TableCell align="center" sx={{ minWidth: 140 }}><strong>Sơ loại (R1/R2)</strong></TableCell>
                    <TableCell align="center" sx={{ minWidth: 130 }}><strong>Bán kết (R3)</strong></TableCell>
                    <TableCell align="center" sx={{ minWidth: 140 }}><strong>Chung kết (R4)</strong></TableCell>
                    <TableCell align="center" sx={{ minWidth: 90 }}><strong>Hạng</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {progressTeams.map((team) => {
                    const cs = team.competition_status
                    const fr = team.final_rank
                    const preliminary = deriveStageStatus(
                      'preliminary',
                      cs,
                      fr,
                      stagePublishMap.preliminary,
                    )
                    // 2026-05-26: BK/CK dùng outcome per-match từ bracket thay
                    // cho derive theo competition_status (chỉ update khi cả 2
                    // match BK xong → 1 winner sớm vẫn show "Chưa công bố").
                    // Fallback: nếu bracket draft mới đè confirmed (matches
                    // không có winner) → suy từ team.competition_status/
                    // final_rank: cs='advanced_to_final' → đã thắng BK; có
                    // final_rank → đã đấu CK.
                    const outcome = teamStageOutcome.get(team.id) || { semi: 'pending', final: 'pending' }
                    const semiFromCs: 'won' | 'lost' | 'pending' =
                      cs === 'advanced_to_final' || (fr !== null && fr !== undefined)
                        ? 'won'
                        : cs === 'eliminated' && (fr === null || fr === undefined)
                          ? 'lost'
                          : 'pending'
                    const semiOutcome = outcome.semi !== 'pending' ? outcome.semi : semiFromCs
                    const semifinal: { label: string; color: 'success' | 'error' | 'warning' | 'default' | 'info'; icon?: boolean } =
                      semiOutcome === 'won'
                        ? { label: 'Qua vòng', color: 'success' }
                        : semiOutcome === 'lost'
                          ? { label: 'Loại (BK)', color: 'error' }
                          : { label: 'Chưa công bố', color: 'default' }
                    const final: { label: string; color: 'success' | 'error' | 'warning' | 'default' | 'info'; icon?: boolean } =
                      fr === 1
                        ? { label: 'Vô địch', color: 'warning', icon: true }
                        : fr === 2
                          ? { label: 'Á quân', color: 'warning' }
                          : outcome.final === 'won'
                            ? { label: 'Qua vòng', color: 'success' }
                            : outcome.final === 'lost'
                              ? { label: 'Không vào CK', color: 'default' }
                              : { label: 'Chưa công bố', color: 'default' }
                    // Hạng cuối chỉ hiển thị khi CK đã có winner (per-match).
                    const rankPublished = outcome.final === 'won' || outcome.final === 'lost' || fr === 1 || fr === 2
                    return (
                      <TableRow key={team.id} hover>
                        <TableCell>{team.name}</TableCell>
                        <TableCell>{team.university}</TableCell>
                        <TableCell align="center">
                          <Chip size="small" label={preliminary.label} color={preliminary.color} />
                        </TableCell>
                        <TableCell align="center">
                          <Chip size="small" label={semifinal.label} color={semifinal.color} />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={final.label}
                            color={final.color}
                            icon={final.icon ? <EmojiEvents fontSize="small" /> : undefined}
                          />
                        </TableCell>
                        <TableCell align="center">
                          {fr && rankPublished
                            ? <strong>#{fr}</strong>
                            : <span style={{ opacity: 0.5 }}>—</span>}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}
